import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { configurationApi, fileApi } from "~/api";
import { folderKeys } from "~/components/side-bar/hooks/use-folder-mutations";
import { replaceOptimisticItem } from "~/hooks/helpers";
import type { Folder } from "~/types/api";
import type { TaskManagerDTO } from "~/types/api-custom";
import { sidebarStorage } from "~/utils/sidebar-storage";
import { addTaskToFolder, replaceTaskIdInFolder, updateFolderCache } from "./create-task-helpers";
import { getTasksQueryKey, TASK_TOAST_MESSAGES } from "./task-constants";
import { useTaskDialog } from "./use-task-dialog";

type CreateTaskVariables = {
  body: {
    workflowId: number;
    brandId: number;
    name: string;
    path: string;
    taskFields: { fieldId: number; value: string }[];
  };
  folderId?: number;
};

const createTaskRequest = async ({ body, folderId }: CreateTaskVariables) => {
  const taskResponse = await fileApi.client.POST("/api/Task", { body });
  if (taskResponse.error) {
    throw taskResponse.error || new Error("Task not found");
  }

  const createdTask = taskResponse.data;

  if (folderId && createdTask?.id) {
    const folderResponse = await configurationApi.client.PUT(
      "/api/brand/{brandId}/Folder/{folderId}/add-tasks",
      {
        params: { path: { brandId: body.brandId, folderId } },
        body: { folderTasks: [{ folderId, taskId: createdTask.id }] },
      }
    );
    if (folderResponse.error) {
      const errorMessage =
        typeof folderResponse.error === "object" && folderResponse.error !== null
          ? JSON.stringify(folderResponse.error)
          : String(folderResponse.error);
      throw new Error(errorMessage);
    }
  }

  return createdTask;
};

export const useCreateTask = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { closeDialog } = useTaskDialog();

  const tasksQueryKey = getTasksQueryKey();

  const { mutateAsync: mutateTask, isPending } = useMutation({
    mutationFn: createTaskRequest,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: tasksQueryKey });
      const previousTasks = queryClient.getQueryData(tasksQueryKey);

      const tempTaskId = -Date.now();
      queryClient.setQueryData<TaskManagerDTO[]>(tasksQueryKey, (old) =>
        replaceOptimisticItem(old, {
          matchOptimistic: () => false, //Trick to add without replacing
          replaceWith: { id: tempTaskId, active: true, ...variables.body },
        })
      );

      let previousFolders: Folder[] | undefined;
      if (variables.folderId) {
        const folderQueryKey = folderKeys.byBrand(variables.body.brandId);
        await queryClient.cancelQueries({ queryKey: folderQueryKey });
        previousFolders = queryClient.getQueryData<Folder[]>(folderQueryKey);
        if (previousFolders) {
          updateFolderCache(queryClient, variables.body.brandId, (folders) =>
            addTaskToFolder(folders, variables.folderId!, tempTaskId)
          );
        }
      }

      return { previousTasks, previousFolders, tempTaskId };
    },
    onError: (err, vars, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(tasksQueryKey, context.previousTasks);
      }

      if (context?.previousFolders && vars.folderId) {
        const folderQueryKey = folderKeys.byBrand(vars.body.brandId);
        queryClient.setQueryData(folderQueryKey, context.previousFolders);
        sidebarStorage.cacheFolders(vars.body.brandId, context.previousFolders);
      }

      const errorMessage = err instanceof Error ? err.message : String(err);
      showToast("error", errorMessage || TASK_TOAST_MESSAGES.CREATE_ERROR, "Error");
    },
    onSuccess: (data, vars, context) => {
      if (!data?.id || !context?.tempTaskId) {
        showToast("success", TASK_TOAST_MESSAGES.CREATE_SUCCESS, "Success");
        closeDialog?.();
        return;
      }

      queryClient.setQueryData<TaskManagerDTO[]>(tasksQueryKey, (old) =>
        replaceOptimisticItem(old, {
          matchOptimistic: (task) => task.id === context.tempTaskId,
          replaceWith: { ...data },
        })
      );

      if (vars.folderId) {
        updateFolderCache(queryClient, vars.body.brandId, (folders) =>
          replaceTaskIdInFolder(folders, vars.folderId!, context.tempTaskId, data.id)
        );
      }

      showToast("success", TASK_TOAST_MESSAGES.CREATE_SUCCESS, "Success");
      closeDialog?.();
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: tasksQueryKey });
      if (variables.folderId) {
        queryClient.invalidateQueries({
          queryKey: folderKeys.byBrand(variables.body.brandId),
        });
      }
    },
  });

  const handleCreate = async (taskData: TaskManagerDTO) => {
    await mutateTask({
      body: {
        workflowId: taskData.workflowId,
        brandId: taskData.brandId,
        name: taskData.name,
        path: taskData.path,
        taskFields: (taskData.taskFields || []).map((field) => ({
          fieldId: field.fieldId,
          value: field.value,
        })),
      },
      folderId: taskData.folder?.id ? Number(taskData.folder.id) : undefined,
    });
  };

  return { handleCreate, isPending };
};
